import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { getProductionLabel, MILL_1 } from "@/setup/mill-config/mill-config";
import { routeStackerProduction } from "@/setup/routes/stacker-pulpit.routes";
import { pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	getEquivalentShiftForAsset,
	getMomentTz,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useHasProfile,
	useShiftNavigator,
	useUserContext,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Add } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StackerBundlesGrid } from "./components/bundles/StackerBundlesGrid";
import { StackerReviewGrid } from "./components/review/StackerReviewGrid";
import { StackerPiecesGrid } from "./components/pieces/StackerPiecesGrid";
import { StackerHeatPanel } from "./components/StackerHeatPanel";
import { StackerShiftRemarkField } from "./components/StackerShiftRemarkField";
import {
	BundleAction,
	FinishedGroup,
	ScrapAction,
} from "./models/FinishedBundle";
import {
	STACKER_BUNDLE_DRAFT_INITIAL,
	STACKER_PIECES_DRAFT_INITIAL,
	StackerBundle,
	StackerBundleDraft,
} from "./models/StackerBundle";
import {
	deleteStackerBundle,
	getStackerReport,
	StackerReport,
	checkBundles,
	setBundlesActive,
	setScrapPiecesActive,
	upsertBundles,
	upsertScrapPieces,
	upsertStackerBundle,
	upsertStackerShiftRemark,
} from "./repositories/StackerPulpitRepository";

const moment = getMomentTz();

export const useStackerReport = ({
	shiftId,
}: {
	shiftId: number | undefined;
}) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	return useQuery<StackerReport, Error>({
		queryKey: ["stacker-report", shiftId, defaultAreaId],
		queryFn: ({ signal }) =>
			getStackerReport(
				{ shiftId: shiftId || 0, defaultAreaId: Number(defaultAreaId) },
				signal,
			),
		enabled: !!shiftId,
	});
};

export const StackerPulpitPage = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const { state } = useUTLSettingsContext();
	const graceMinutes = Number(
		get(state, `settings.STACKER_EDIT_GRACE_MINUTES`, 15),
	);

	const assetId = Number(defaultAreaId);
	const isM1 = assetId === MILL_1.id;
	const productionLabel = getProductionLabel(defaultAreaId);

	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const hasProfile = useHasProfile();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const shiftBelongsToAsset = (shiftInfo?.AssetId ?? assetId) === assetId;
	const currentShiftId = shiftBelongsToAsset
		? shiftInfo?.CurrentShiftId
		: undefined;
	const shiftEndTs = shiftInfo?.CurrentEnd
		? new Date(shiftInfo.CurrentEnd).getTime()
		: null;

	const isOperatorOnly =
		hasProfile("Operator Profile") &&
		!hasProfile("Clerk Profile") &&
		!hasProfile("Supervisor Profile") &&
		!hasProfile("SubAdmin Profile") &&
		!hasProfile("admin Profile");

	const [editingId, setEditingId] = useState<number | null>(null);
	const [addingBundle, setAddingBundle] = useState(false);
	const [draftRow, setDraftRow] = useState<StackerBundleDraft | null>(null);
	const [error, setError] = useState("");
	const [bundleToDelete, setBundleToDelete] = useState<StackerBundle | null>(
		null,
	);

	const {
		data: report,
		isLoading,
		isFetching,
		isError,
		error: e,
	} = useStackerReport({
		shiftId: currentShiftId,
	});

	const bundles = report?.bundles || [];
	const remark = report?.remark ?? null;
	const heat = report?.heat ?? null;
	const isPlcShift = !isM1 && report?.shiftSource?.Source === "P";
	const isLegacyM2Shift = !isM1 && !isPlcShift;

	const editGraceMs = graceMinutes * 60 * 1000;

	const isReadOnly =
		isOperatorOnly &&
		shiftEndTs !== null &&
		moment.now() > shiftEndTs + editGraceMs;

	const upsertBundle = useMutation(upsertStackerBundle, {
		meta: { successMessage: "The row was saved successfully" },
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["stacker-report", currentShiftId],
			});
		},
		onError: (error: Error) => setError(error.message),
	});
	const deleteBundle = useMutation(deleteStackerBundle, {
		meta: { successMessage: "The row was deleted successfully" },
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["stacker-report", currentShiftId],
			});
		},
		onError: (error: Error) => setError(error.message),
	});
	const upsertRemark = useMutation(upsertStackerShiftRemark, {
		meta: { successMessage: "Remark saved successfully" },
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["stacker-report", currentShiftId],
			});
		},
		onError: (error: Error) => setError(error.message),
	});

	// Edit, delete / restore, mark checked and scrap (M2.UpsertBundles, M2.UpsertScrapPieces)
	const canEditBundles =
		hasProfile("Clerk Profile") ||
		hasProfile("Supervisor Profile") ||
		hasProfile("SubAdmin Profile") ||
		hasProfile("admin Profile");

	const reviewBundles = useMutation(
		(action: BundleAction) => {
			if (action.type === "active")
				return setBundlesActive({
					bundleIds: action.ids,
					isActive: action.isActive,
				});
			if (action.type === "check")
				return checkBundles({ bundleIds: action.ids });
			const { type, ...values } = action;
			return type === "edit"
				? upsertBundles({ ...values, bundleIds: action.ids })
				: upsertBundles({ ...values, shiftId: currentShiftId });
		},
		{
			meta: { successMessage: "The bundles were updated successfully" },
			// Wait for the refetch: the panel stays locked until the new data is shown (no flash of the old row)
			onSuccess: () =>
				queryClient.invalidateQueries({
					queryKey: ["stacker-report", currentShiftId],
				}),
			onError: (error: Error) => setError(error.message),
		},
	);

	const changeScrap = useMutation(
		(action: ScrapAction) => {
			if (action.type === "remove")
				return setScrapPiecesActive({
					scrapPiecesIds: action.ids,
					isActive: false,
				});
			const { type, ...values } = action;
			return type === "edit"
				? upsertScrapPieces({ ...values, scrapPiecesId: action.id })
				: upsertScrapPieces({ ...values, shiftId: currentShiftId });
		},
		{
			meta: { successMessage: "The scrap was saved successfully" },
			// Wait for the refetch: the panel stays locked until the new data is shown (no flash of the old row)
			onSuccess: () =>
				queryClient.invalidateQueries({
					queryKey: ["stacker-report", currentShiftId],
				}),
			onError: (error: Error) => setError(error.message),
		},
	);

	const isBundleMutating =
		upsertBundle.isPending ||
		deleteBundle.isPending ||
		reviewBundles.isPending ||
		changeScrap.isPending;

	const isTotalsLoading = isBundleMutating || isFetching;

	const startEdit = (b: StackerBundle) => {
		if (isReadOnly) return;
		setEditingId(b.StackerBundleId);

		if (isM1) {
			setDraftRow({
				...STACKER_PIECES_DRAFT_INITIAL,
				StackerBundleId: b.StackerBundleId,
				ShiftId: b.ShiftId,
				SectionId: b.SectionId,
				GradeId: b.GradeId,
				HeatId: b.HeatId,
				LengthInches: b.LengthInches,
				GoodPieces: b.GoodPieces ?? 0,
				TotalWeight: b.TotalWeight,
				ScrapPieces: b.ScrapPieces ?? null,
				ScrapReason: b.ScrapReason,
			});
			return;
		}

		const auto = (b.GoodBundles || 0) * (b.GoodPiecesPerBundle || 0);
		setDraftRow({
			StackerBundleId: b.StackerBundleId,
			ShiftId: b.ShiftId,
			SectionId: b.SectionId,
			GradeId: b.GradeId,
			HeatId: b.HeatId,
			LengthInches: b.LengthInches,
			GoodBundles: b.GoodBundles,
			GoodPiecesPerBundle: b.GoodPiecesPerBundle,
			TotalPieces: b.TotalPieces,
			TotalPiecesIsManual: b.TotalPieces !== auto,
			TotalWeight: b.TotalWeight,
			ScrapBundles: b.ScrapBundles,
			ScrapPiecesPerBundle: b.ScrapPiecesPerBundle,
			ScrapReason: b.ScrapReason,
		});
	};

	const startAdd = () => {
		if (!currentShiftId || isReadOnly) return;
		setEditingId(null);
		setDraftRow({
			...(isM1 ? STACKER_PIECES_DRAFT_INITIAL : STACKER_BUNDLE_DRAFT_INITIAL),
			ShiftId: currentShiftId,
		});
	};

	const handleSaveDraft = (draft: StackerBundleDraft) => {
		if (draftRow) {
			upsertBundle.mutate({ draft, defaultAreaId: Number(defaultAreaId) });
		}
		setDraftRow(null);
		setEditingId(null);
	};

	const quickSaveBundle = (
		bundle: StackerBundle,
		patch: {
			GoodPieces: number;
			ScrapPieces: number | null;
			TotalWeight: number | null;
		},
	) => {
		if (isReadOnly) return;
		upsertBundle.mutate({
			draft: {
				...STACKER_PIECES_DRAFT_INITIAL,
				StackerBundleId: bundle.StackerBundleId,
				ShiftId: bundle.ShiftId,
				SectionId: bundle.SectionId,
				GradeId: bundle.GradeId,
				HeatId: bundle.HeatId,
				LengthInches: bundle.LengthInches,
				GoodPieces: patch.GoodPieces,
				TotalWeight: patch.TotalWeight,
				ScrapPieces: patch.ScrapPieces,
				ScrapReason: bundle.ScrapReason,
			},
			defaultAreaId: Number(defaultAreaId),
		});
	};

	const cancelEdit = () => {
		setEditingId(null);
		setDraftRow(null);
	};

	useEffect(() => {
		if (shiftInfo) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			navigate(`${routeStackerProduction}/${shiftId}`, { replace: true });
			cancelEdit();
			setAddingBundle(false);
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (!assetId) return;
		(async () => {
			if (shiftIdParam) {
				const equivalent = await getEquivalentShiftForAsset(
					Number(shiftIdParam),
					assetId,
				);
				if (equivalent) {
					await getShiftDataFromAPI(null, equivalent.ShiftId, assetId);
					return;
				}
			}
			const productionDate = shiftInfo?.CurrentProductionDate ?? null;
			await getShiftDataFromAPI(productionDate, 0, assetId);
		})();
	}, [assetId]);

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [e, isError]);

	const remarkField = (
		<StackerShiftRemarkField
			remark={remark}
			disabled={!currentShiftId || isReadOnly}
			isMutating={upsertRemark.isLoading}
			onSave={(comment) => {
				if (!currentShiftId) return;
				upsertRemark.mutate({
					shiftId: currentShiftId,
					comment,
					defaultAreaId: Number(defaultAreaId),
				});
			}}
		/>
	);

	return (
		<>
			<HelmetDexteel title={productionLabel} />
			<Box
				sx={{
					backgroundColor: pageBgColor,
					height: "calc(100vh - 100px)",
					display: "flex",
					flexDirection: "column",
					gap: 2,
					px: 2,
					py: 2,
					boxSizing: "border-box",
				}}
			>
				<Box sx={{ flex: "0 0 auto" }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-end",
							flexWrap: "wrap",
							gap: 2,
						}}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								gap: 1,
							}}
						>
							<Typography variant="h5" sx={{ fontWeight: 600 }}>
								{productionLabel}
							</Typography>
							<ShiftDayNavigatorControl
								preventInit
								assetId={Number(defaultAreaId)}
							/>
						</Box>
						<Box
							sx={{
								ml: "auto",
								alignSelf: "stretch",
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-end",
								justifyContent: "space-between",
								gap: 1,
							}}
						>
							<StackerHeatPanel heat={heat} />
							{isM1 && (
								<Button
									variant="contained"
									startIcon={<Add />}
									onClick={startAdd}
									sx={{ display: isFetching ? "none" : "inline-flex" }}
									disabled={
										!currentShiftId ||
										draftRow !== null ||
										editingId !== null ||
										isBundleMutating ||
										isReadOnly
									}
								>
									Add Row
								</Button>
							)}
							{isPlcShift && canEditBundles && (
								<Button
									variant="contained"
									startIcon={<Add />}
									onClick={() => setAddingBundle(true)}
									disabled={!currentShiftId || addingBundle || isBundleMutating}
									data-testid="bundle-add-manual"
								>
									Add Manual Bundle
								</Button>
							)}
							{isLegacyM2Shift && report?.shiftSource && (
								<Typography
									variant="caption"
									color="textDisabled"
									sx={{ whiteSpace: "nowrap" }}
								>
									Shift before the PLC tracking (
									{moment.utc(report.shiftSource.PlcStart).format("MM/DD/YYYY")}
									): manual entries, read-only.
								</Typography>
							)}
							{isReadOnly && (
								<Typography
									variant="caption"
									color="textDisabled"
									sx={{ whiteSpace: "nowrap" }}
								>
									The editing window for this shift has closed - you can no
									longer add or edit rows.
								</Typography>
							)}
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						flex: "1 1 0",
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
					}}
				>
					{isLoading ? (
						<Box sx={{ height: 200 }}>
							<CenteredLazyLoading />
						</Box>
					) : isM1 ? (
						<StackerPiecesGrid
							rows={bundles}
							shiftId={currentShiftId || 0}
							draftRow={draftRow}
							editingId={editingId}
							isMutating={isBundleMutating}
							isTotalsLoading={isTotalsLoading}
							readOnly={isReadOnly}
							onStartEdit={startEdit}
							onCancelEdit={cancelEdit}
							onDraftChange={setDraftRow}
							onSaveDraft={handleSaveDraft}
							onDelete={(s) => setBundleToDelete(s)}
							onQuickSave={quickSaveBundle}
						/>
					) : isPlcShift ? (
						<StackerReviewGrid
							groups={bundles as FinishedGroup[]}
							details={report?.details || []}
							scrap={report?.scrap || []}
							canEdit={canEditBundles}
							isMutating={isBundleMutating}
							isTotalsLoading={isTotalsLoading}
							remark={remarkField}
							adding={addingBundle}
							shiftId={currentShiftId}
							shift={
								shiftInfo?.CurrentStart && shiftInfo?.CurrentEnd
									? {
											start: new Date(shiftInfo.CurrentStart),
											end: new Date(shiftInfo.CurrentEnd),
										}
									: null
							}
							shiftHeatIds={(report?.heatOptions || []).map((h) => h.HeatId)}
							onAddingChange={setAddingBundle}
							onAction={(args) => reviewBundles.mutate(args)}
							onScrapAction={(action) => changeScrap.mutate(action)}
						/>
					) : (
						<StackerBundlesGrid
							rows={bundles}
							shiftId={currentShiftId || 0}
							draftRow={draftRow}
							editingId={editingId}
							isMutating={isBundleMutating}
							isTotalsLoading={isTotalsLoading}
							readOnly
							onStartEdit={startEdit}
							onCancelEdit={cancelEdit}
							onDraftChange={setDraftRow}
							onSaveDraft={handleSaveDraft}
							onDelete={(s) => setBundleToDelete(s)}
						/>
					)}
				</Box>

				{!isPlcShift && <Box sx={{ flex: "0 0 auto" }}>{remarkField}</Box>}
			</Box>

			<ConfirmationDialogRaw
				title="Confirm Deletion"
				message={
					bundleToDelete
						? `Are you sure you want to delete the bundle with section ${bundleToDelete.SectionName}, ${
								bundleToDelete.HeatName
									? `heat ${bundleToDelete.HeatName}, `
									: ""
							}${
								bundleToDelete.GradeName
									? `grade ${bundleToDelete.GradeName}, `
									: ""
							}length ${Math.floor(
								bundleToDelete.LengthInches / 12,
							)}' ${bundleToDelete.LengthInches % 12}" and ${
								bundleToDelete.TotalPieces ?? bundleToDelete.GoodPieces ?? 0
							} total pieces? This action cannot be undone.`
						: ""
				}
				open={!!bundleToDelete}
				onClose={(value?: string) => {
					if (value == "OK") {
						deleteBundle.mutate({
							stackerBundleId: bundleToDelete?.StackerBundleId!,
							defaultAreaId: Number(defaultAreaId),
						});
					}
					setBundleToDelete(null);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
